import React, { useState, useEffect } from "react";

import withErrorHandling from "../HOC/ErrorHandling";
import { useSharedProps } from "../HOC/SharedProps";

import { Box, Button, IconButton, TextField, MenuItem, Typography, useTheme } from "@mui/material";

import { MoreHoriz } from "@mui/icons-material";

import { motion } from "framer-motion";

import { CopyBlock, dracula } from "react-code-blocks";

import {
  brandButton,
  darkSurface,
  heroText,
  motionVariants,
  pageBackground,
} from "../helpers/designSystem";

import t from "../helpers/en";
import {
  EDIT_ITEM,
  DELETE_ITEM,
  JSON_ITEM,
  NEW_ITEM,
  ROWS_PARAMS_LOGS,
  PAGE_SIZE,
} from "../helpers/constants";
import {
  deleteImage,
  getImages,
  newImage,
  updateImage,
  getServiceAccounts,
  getBuilders,
  getk8sLogs,
  forceRebuild,
} from "../helpers/utils";
import { filterRecords, changeTxt, selectOption } from "../helpers/selectors";
import usePopup from "../helpers/contexts/usePopup";

import Select from "../components/Inputs/Select";
import Table from "../components/Table/Table";
import Modal from "../components/Modal/Modal";
import PopupWindow from "../components/PopupWindow";

import {
  styleEllipsis,
  styleTableMenuOption,
  styleStatus,
  styleButton,
} from "../GeneralStyles";

const AUDIT_LOGS = "AUDIT_LOGS";
const FORCE_BUILD = "FORCE_BUILD";

const rowsParams = (data) => ({
  header: [
    t.name,
    t.latestImage,
    t.generation,
    t.status,
    t.resourceVersion,
    t.createdOn,
  ],
  body: data,
  bodyCount: data,

  bodyParams: (val) => [
    { value: val?.metadata?.displayName },
    {
      value: val?.status?.latestImage,
      specialDetails: {
        hasTooltip: true,
        isUrl: true,
        ellipsis: { maxWidth: "10em", ...styleEllipsis },
      },
    },
    { value: val?.metadata?.generation },
    {
      value: (
        <span
          style={{
            ...styleStatus(val?.status?.conditions?.[0]?.status),
          }}
        >
          {val?.status?.conditions?.[0]?.status}
        </span>
      ),
    },
    { value: val?.metadata?.resourceVersion },
    {
      value: new Date(val?.metadata?.creationTimestamp).toLocaleString("en-US"),
    },
  ],
});

const Images = (props) => {
  const { showError } = props;

  const [data, setData] = useState({
    data: [],
    filtered: [],
  });
  const [filters, setFilters] = useState({});
  const [form, setForm] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [buildersList, setBuildersList] = useState([]);
  const [serviceAccounts, setServiceAccounts] = useState([]);
  const [pressedBtn, setPressedBtn] = useState(null);

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const { currentGroup } = useSharedProps();
  const {
    buttonRef,
    popupRef,
    popupPosition,
    openPopup,
    closePopup,
    onMouseLeave,
    openPopupForItem,
  } = usePopup(100);

  const fetchData = (cb, onError) => {
    getImages(null, (result, err) => {
      if (!err) {
        setData({ data: result, filtered: result });

        cb && cb(result);
      } else {
        onError(err);
      }
    });
  };

  useEffect(() => {
    const callback = (cb) => {
      fetchData(cb, showError);

      getServiceAccounts((result, err) => {
        if (Array.isArray(result)) {
          const updateResult = result.map((it) => ({
            displayName: it?.metadata?.displayName,
            name: it?.metadata?.name,
          }));
          setServiceAccounts(updateResult);
        } else {
          showError(err);
        }
      });

      getBuilders((result, err) => {
        if (Array.isArray(result)) {
          const updateResult = result.map((it) => ({
            displayName: it?.metadata?.displayName,
            name: it?.metadata?.name,
          }));
          setBuildersList(updateResult);
        } else {
          showError(err);
        }
      });
    };

    setIsLoading(true);
    callback(() => setIsLoading(false));
  }, [showError, currentGroup]);

  const onOpen = (evt, row) => {
    const { id } = evt.target;
    let modalData = {};

    if (id === AUDIT_LOGS) {
      new Promise((resolve) => {
        getk8sLogs(
          {
            objectName: row?.metadata?.name,
            pageNumber: 0,
            pageSize: PAGE_SIZE,
          },
          (result) => {
            resolve({ ...row, logs: [...(result.content ?? [])] });
          }
        );
      }).then((res) => setForm(res));
    } else if (id === FORCE_BUILD) {
      forceRebuild(row?.metadata, (result, err) => {
        if (err) {
          showError(err);
        }
      });

      return;
    } else {
      modalData = {
        ...row,
        ...(id === EDIT_ITEM
          ? {
              displayName: row?.metadata?.displayName,
              name: row?.metadata?.name,
              tag: row?.spec?.tag,
              resourceVersion: row?.metadata?.resourceVersion,
              serviceAccountName: row?.spec?.serviceAccountName,
              builderName: row?.spec?.builder?.name,
              gitRepo: row?.spec?.source?.git?.url,
              gitRevision: row?.spec?.source?.git?.revision,
            }
          : {}),
      };

      setForm(modalData);
    }

    setPressedBtn(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({});
  };

  const onDelete = (evt, row) => {
    if (form?.isSubmitting) return;

    setForm((prev) => ({ ...prev, isSubmitting: true }));

    deleteImage(row.metadata.name, (result, err) => {
      if (!err) {
        triggerUpdate();
      } else {
        setForm((prev) => ({ ...prev, isSubmitting: false }));
        showError(err);
      }
    });
  };

  const triggerUpdate = () => {
    fetchData((result) => {
      onFilterRecords(result, filters);
    }, showError);
    handleClose();
  };

  const onChangeRowsPerPage = (evt) => {
    setRowsPerPage(parseInt(evt.target.value, 10));
    setPage(0);
  };

  const onChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const onFilterRecords = (result, currentFilters) => {
    const res = filterRecords(result, currentFilters, (item) => [
      item.metadata?.displayName,
    ]);

    setData(res);
  };

  const onKeyPressSearch = (evt) => {
    evt.preventDefault();

    if (evt.key === "Enter") {
      onFilterRecords(data?.data, filters);
      setPage(0);
    }
  };

  const onChangeSearchTxt = (evt) => {
    const { value } = evt.target;

    setFilters({ ...filters, searchedValue: value });
  };

  const onClearSearchTxt = () => {
    const copyFilters = { ...filters, searchedValue: null };

    setFilters(copyFilters);
    onFilterRecords(data?.data, copyFilters);
  };

  const onChangeTxt = (evt) => {
    const result = changeTxt(evt, form);

    setForm(result);
  };

  const onSelectOption = (_, value, param) => {
    let result = selectOption(_, value, param, form);

    setForm(result);
  };

  const onSave = (action) => {
    if (form?.isSubmitting) return;

    setForm((prev) => ({ ...prev, isSubmitting: true }));

    action(form, (result, err) => {
      if (!err) {
        triggerUpdate();
      } else {
        setForm((prev) => ({ ...prev, isSubmitting: false }));
        showError(err);
      }
    });
  };

  let title, content, saveLabel, onConfirmModalAction;

  const formElement = () => (
    <Box sx={{ display: "grid", gridRowGap: "1rem" }}>
      <TextField
        id={`outlined-basic-displayName`}
        label={t.name}
        name="displayName"
        value={form?.displayName || ""}
        onChange={onChangeTxt}
        InputProps={{
          readOnly: !!form?.resourceVersion,
        }}
        size="small"
      />
      <TextField
        id={`outlined-basic-tag`}
        label={t.tag}
        name="tag"
        value={form?.tag || ""}
        onChange={onChangeTxt}
        size="small"
      />
      <Select
        options={serviceAccounts}
        onChange={(evt, val) =>
          onSelectOption(evt, val?.name, "serviceAccountName")
        }
        label={t.serviceAccount}
        item="displayName"
        value={
          serviceAccounts?.find((it) => it.name === form?.serviceAccountName) ||
          ""
        }
      />
      <Select
        options={buildersList}
        onChange={(evt, val) => onSelectOption(evt, val?.name, "builderName")}
        label={t.build}
        item="displayName"
        value={buildersList?.find((it) => it.name === form?.builderName) || ""}
      />
      <TextField
        id="outlined-basic-gitRepo"
        label={t.repository}
        name="gitRepo"
        value={form?.gitRepo || ""}
        onChange={onChangeTxt}
        size="small"
      />
      <TextField
        id="outlined-basic-gitRevision"
        label={t.revision}
        name="gitRevision"
        value={form?.gitRevision || ""}
        onChange={onChangeTxt}
        size="small"
      />
    </Box>
  );

  switch (pressedBtn) {
    case JSON_ITEM:
      title = t.json;
      content = (
        <Box>
          <CopyBlock
            customStyle={{ height: "550px", width: "100%", overflow: "scroll" }}
            text={JSON.stringify(form, null, 4)}
            language={"en"}
            showLineNumbers={true}
            theme={dracula}
          />
        </Box>
      );
      break;
    case NEW_ITEM:
      title = t.create;
      content = formElement();
      onConfirmModalAction = () => onSave(newImage);
      saveLabel = t.save;
      break;
    case EDIT_ITEM:
      title = t.update;
      content = formElement();
      onConfirmModalAction = () => onSave(updateImage);
      saveLabel = t.save;
      break;
    case DELETE_ITEM:
      title = t.delete(t.image);
      onConfirmModalAction = (e) => onDelete(e, form);
      saveLabel = t.confirm;
      break;
    case AUDIT_LOGS:
      title = t.events;
      content = (
        <Table
          rows={{
            ...ROWS_PARAMS_LOGS(form.logs ?? []),
            body: form?.logs ?? [],
          }}
          selectedRow={() => false}
          hideTableHeader={true}
        />
      );
      break;
    default:
  }

  return (
    <Box sx={{ ...pageBackground(isDarkMode), p: { xs: 2, md: 3 } }}>
      <motion.div {...motionVariants.heroEnter}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h3" sx={{ ...heroText(isDarkMode), mb: 1 }}>
            App images
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: isDarkMode ? darkSurface.textMuted : "text.secondary" }}
          >
            Built images stored in the platform registry.
          </Typography>
        </Box>
      </motion.div>

      <Button
        sx={{ mb: 2, ...styleButton, ...brandButton() }}
        variant="contained"
        onClick={(evt) => onOpen(evt, null)}
        id={NEW_ITEM}
        disabled={currentGroup?.id && !currentGroup?.isContributor2Group}
      >
        {t.addNew}
      </Button>

      <Table
        rows={{
          ...rowsParams(data?.filtered),
          body: data?.filtered?.slice(
            page * rowsPerPage,
            page * rowsPerPage + rowsPerPage
          ),
        }}
        selectedRow={(item) => {
          return item?.metadata?.name === form?.metadata?.name;
        }}
        {...{ onChangeSearchTxt, onKeyPressSearch, onClearSearchTxt }}
        searchedValue={filters?.searchedValue}
        {...{
          page,
          rowsPerPage,
          onChangePage,
          onChangeRowsPerPage,
          isLoading,
        }}
        actionBtns={(item) => (
          <IconButton
            sx={{ padding: 0 }}
            ref={buttonRef}
            onClick={(e) => {
              e.stopPropagation();

              openPopup(item, e.currentTarget);
            }}
          >
            <MoreHoriz sx={{ color: (theme) => theme.palette.primary.main }} />
          </IconButton>
        )}
      />

      {openPopupForItem && (
        <div ref={popupRef} onMouseEnter={() => {}} {...{ onMouseLeave }}>
          <PopupWindow
            specificDetails={{
              onClose: closePopup,
              popupPosition: popupPosition,
              style: { div: { width: "140px" } },
              actions: [
                {
                  id: FORCE_BUILD,
                  label: t.forceBuild,
                  onClick: (evt) => onOpen(evt, openPopupForItem),
                  disabled:
                    currentGroup?.id && !currentGroup?.isContributor2Group,
                },
                {
                  id: JSON_ITEM,
                  label: t.json,
                  onClick: (evt) => onOpen(evt, openPopupForItem),
                },
                {
                  id: AUDIT_LOGS,
                  label: `${t.view} ${t.events}`,
                  onClick: (evt) => onOpen(evt, openPopupForItem),
                },
                ...(!openPopupForItem?.metadata?.labels?.default
                  ? [
                      {
                        id: EDIT_ITEM,
                        label: t.update,
                        onClick: (evt) => onOpen(evt, openPopupForItem),
                        disabled:
                          currentGroup?.id &&
                          !currentGroup?.isContributor2Group,
                      },
                      {
                        id: DELETE_ITEM,
                        label: t.destroy,
                        onClick: (evt) => onOpen(evt, openPopupForItem),
                        disabled:
                          currentGroup?.id &&
                          !currentGroup?.isContributor2Group,
                      },
                    ]
                  : []),
              ]?.map((item, idx) => (
                <MenuItem
                  sx={{ ...styleTableMenuOption }}
                  key={idx}
                  id={item.id}
                  onClick={(evt) => {
                    if (item.onClick) {
                      item.onClick(evt);
                      closePopup();
                    }
                  }}
                  disabled={item.disabled}
                >
                  {item.label}
                </MenuItem>
              )),
            }}
          />
        </div>
      )}

      <Modal
        {...{ open }}
        handleClose={handleClose}
        title={title}
        closeLabel={t.close}
        onSave={onConfirmModalAction}
        specificDetails={{ isSubmitting: form?.isSubmitting }}
        {...{ content, saveLabel }}
      />
    </Box>
  );
};

export default withErrorHandling(Images);
